<template>
    <div class="music-playlist-container">
        <!-- 页面头部 -->
        <div class="page-header">
            <h1>音乐歌单管理</h1>
        </div>

        <!-- 筛选工具栏 -->
        <div class="toolbar">
            <el-input v-model="searchQuery" placeholder="搜索歌曲" clearable class="search-input">
                <template #append>
                    <el-button @click="fetchMusicList">
                        <el-icon>
                            <Search />
                        </el-icon>
                    </el-button>
                </template>
            </el-input>

            <!-- 优化分类选择交互：加载状态 + 空状态兜底 -->
            <el-select v-model="selectedCategory" placeholder="选择分类" class="category-select"
                :disabled="isCategoryLoading">
                <el-option label="全部" value="" />
                <el-option v-for="category in categories" :key="category.value" :label="category.label"
                    :value="category.value" />
            </el-select>
        </div>

        <!-- 数据加载状态 -->
        <div class="loading-state" v-if="loading">
            <el-skeleton :count="6" animated />
        </div>

        <!-- 歌单列表 -->
        <div class="music-list" v-else>
            <div v-if="musicList.length === 0" class="empty-state">
                <el-icon class="empty-icon">
                    <Music />
                </el-icon>
                <h3>暂无歌曲</h3>
                <p>点击"添加歌曲"按钮开始创建你的歌单</p>
            </div>

            <el-table v-if="musicList.length != 0" :data="filteredMusicList" border stripe style="width: 100%;">
                <el-table-column prop="music" label="歌名" min-width="200" />
                <el-table-column label="分类">
                    <template #default="scope">
                        <el-tag :type="getCategoryType(scope.row.class)">{{ scope.row.class }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="付费状态" width="100">
                    <template #default="scope">
                        <el-tag :type="getPayType(scope.row.pay)">{{ scope.row.pay || '免费' }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="180">
                    <template #default="scope">
                        <el-button size="small" type="primary" @click="editSong(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>编辑
                        </el-button>
                        <el-button size="small" type="danger" @click="deleteSong(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 编辑歌曲对话框 -->
        <el-dialog v-model="editDialogVisible" title="编辑歌曲信息" width="500px">
            <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-width="100px">
                <el-form-item label="歌曲名称" prop="title">
                    <el-input v-model="editForm.title" placeholder="请输入歌曲名称" clearable />
                </el-form-item>

                <el-form-item label="歌曲分类" prop="classId">
                    <el-select v-model="editForm.classId" placeholder="请选择歌曲分类" style="width: 100%">
                        <el-option v-for="category in categories" :key="category.value" :label="category.label"
                            :value="category.value" />
                    </el-select>
                </el-form-item>

                <el-form-item label="付费状态" prop="pay">
                    <el-select v-model="editForm.pay" placeholder="请选择付费状态" style="width: 100%">
                        <el-option label="免费" value="免费" />
                        <el-option label="付费" value="付费" />
                        <el-option label="SC点歌" value="SC点歌" />
                    </el-select>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="editDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmEdit" :loading="editLoading">确认修改</el-button>
            </template>
        </el-dialog>

        <!-- 删除确认对话框 -->
        <el-dialog v-model="deleteDialogVisible" title="确认删除" width="30%">
            <p>确定要删除这首歌曲吗？</p>
            <template #footer>
                <el-button @click="deleteDialogVisible = false">取消</el-button>
                <el-button type="danger" @click="confirmDelete">确认删除</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Music, Delete, Edit } from '@element-plus/icons-vue';
import axios from 'axios';

// 状态管理
const musicList = ref([]);
const categories = ref([]);
const searchQuery = ref('');
const selectedCategory = ref('');
const loading = ref(false);          // 主数据加载状态
const isCategoryLoading = ref(false); // 分类数据独立加载状态
const deleteDialogVisible = ref(false);
const currentDeleteId = ref(null);

// 编辑相关状态
const editDialogVisible = ref(false);
const editLoading = ref(false);
const editFormRef = ref(null);
const currentEditId = ref(null);
const editForm = ref({
    title: '',
    classId: '',
    pay: '免费'
});

// 编辑表单验证规则
const editRules = ref({
    title: [
        { required: true, message: '请输入歌曲名称', trigger: 'blur' },
        { min: 1, max: 50, message: '歌曲名称长度不能超过50个字符', trigger: 'blur' }
    ],
    classId: [
        { required: true, message: '请选择歌曲分类', trigger: 'change' }
    ],
    pay: [
        { required: true, message: '请选择付费状态', trigger: 'change' }
    ]
});

// 计算属性：过滤后的歌曲列表
const filteredMusicList = computed(() => {
    if (!musicList.value.length) return [];

    return musicList.value.filter(song => {
        // 搜索条件：歌名/分类模糊匹配
        const searchMatch = !searchQuery.value ||
            song.music?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            song.class?.toLowerCase().includes(searchQuery.value.toLowerCase());

        // 分类条件：全部/匹配分类值
        const categoryMatch = !selectedCategory.value ||
            song.class === selectedCategory.value;

        return searchMatch && categoryMatch;
    });
});

// 生命周期：初始化加载
onMounted(() => {
    fetchCategoryList(); // 优先加载分类
    fetchMusicList();    // 加载歌曲列表
});

// 监听分类变化：自动触发重新搜索
watch(selectedCategory, () => {
    fetchMusicList();
});

// 获取分类列表（独立接口）
const fetchCategoryList = async () => {
    isCategoryLoading.value = true;
    try {
        const res = await axios.get('/adminapi/class/list');
        // 数据格式化：确保 label/value 结构
        categories.value = res.data.results.map(item => ({
            label: item.class || item.value, // 兼容不同后端结构
            value: item.class || item.value
        }));
    } catch (error) {
        console.error('分类加载失败:', error);
        ElMessage.error('分类加载失败，请重试');
        categories.value = []; // 异常兜底
    } finally {
        isCategoryLoading.value = false;
    }
};

// 获取音乐列表
const fetchMusicList = async () => {
    loading.value = true;
    try {
        const res = await axios.get('/adminapi/music/list', {
            params: {
                category: selectedCategory.value || undefined // 传递分类筛选参数
            }
        });
        musicList.value = res.data.results || [];
    } catch (error) {
        console.error('歌曲加载失败:', error);
        ElMessage.error('歌曲加载失败，请重试');
        musicList.value = []; // 异常兜底
    } finally {
        loading.value = false;
    }
};

// 编辑歌曲
const editSong = (song) => {
    currentEditId.value = song.id;
    editForm.value = {
        title: song.music,
        classId: song.class,
        pay: song.pay || '免费'
    };
    editDialogVisible.value = true;
};

// 确认编辑
const confirmEdit = async () => {
    if (!editFormRef.value) return;

    // 表单验证
    const isValid = await new Promise(resolve => {
        editFormRef.value.validate(valid => resolve(valid));
    });

    if (!isValid) return;

    editLoading.value = true;
    try {
        await axios.put(`/adminapi/music/${currentEditId.value}`, {
            title: editForm.value.title,
            classId: editForm.value.classId,
            pay: editForm.value.pay
        });
        
        ElMessage.success('歌曲信息更新成功');
        editDialogVisible.value = false;
        await fetchMusicList(); // 重新加载数据
    } catch (error) {
        console.error('更新失败:', error);
        ElMessage.error('更新失败，请稍后重试');
    } finally {
        editLoading.value = false;
    }
};

// 删除歌曲
const deleteSong = (id) => {
    currentDeleteId.value = id;
    deleteDialogVisible.value = true;
};

// 确认删除
const confirmDelete = async () => {
    if (!currentDeleteId.value) return;

    try {
        await axios.delete(`/adminapi/music/${currentDeleteId.value}`);
        // 乐观更新 + 重新拉取数据（保证数据一致性）
        musicList.value = musicList.value.filter(song => song.id !== currentDeleteId.value);
        await fetchMusicList(); // 重新加载最新数据
        ElMessage.success('歌曲删除成功');
    } catch (error) {
        console.error('删除失败:', error);
        ElMessage.error('删除失败，请稍后重试');
    } finally {
        deleteDialogVisible.value = false;
    }
};

// 分类标签类型映射
const getCategoryType = (category) => {
    const typeMap = {
        pop: 'primary',
        rock: 'danger',
        jazz: 'warning',
        classical: 'info',
        electronic: 'success'
    };
    return typeMap[category] || 'info';
};

// 付费状态标签类型映射
const getPayType = (payStatus) => {
    const typeMap = {
        '免费': 'success',
        '付费': 'warning',
        'SC点歌': 'danger'
    };
    return typeMap[payStatus] || 'success'; // 默认为免费样式
};
</script>

<style scoped lang="scss">
/* 原有样式保持不变 */
.music-playlist-container {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;

    .page-header {
        text-align: center;
        margin-bottom: 30px;

        h1 {
            font-size: 24px;
            font-weight: 600;
            color: #303133;
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        gap: 15px;

        .search-input {
            flex: 1;
            max-width: 300px;
        }

        .category-select {
            flex: 0 0 120px;
        }
    }

    .loading-state {
        padding: 30px 0;
        text-align: center;
    }

    .music-list {
        .empty-state {
            text-align: center;
            padding: 50px 0;

            .empty-icon {
                font-size: 60px;
                color: #909399;
                margin-bottom: 20px;
            }

            h3 {
                font-size: 18px;
                color: #303133;
                margin-bottom: 10px;
            }

            p {
                color: #909399;
            }
        }

        .el-table {
            --el-table-header-text-color: #303133;
            --el-table-row-hover-bg-color: #f8f9fa;
        }

        .el-table th {
            background-color: #f1f2f3;
        }
    }
}
</style>